import { motion } from "framer-motion"
import ListWithDescription from "@/components/elements/CustomLists/ListWithDescription"
import ImageWithContentLeft from "@/components/elements/ImageWithContent/ImageWithContentLeft"
import Paragraph from "@/components/elements/Paragraph/Paragraph"
import TitleBar from "@/components/elements/TitleBar/TitleBar"
import TitleWithContent from "@/components/elements/TitleWithContent/TitleWithContent"
import CustomLists from "@/components/elements/CustomLists/CustomLists"
import l1 from "@/assets/images/dummy-img.jpg"

// Define fadeInUp animation configuration
const fadeInUp = {
  initial: { opacity: 0, y: 50 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 1, ease: "easeOut" },
  viewport: { once: false, amount: 0.2 },
};

const LiquidWaterproofingMembrane = () => {
  return (
    <>
      <div className="pb-0 px-4  2xl:px-40 xl:px-40 md:px-[3rem] my-[5rem]">
        <TitleBar title="Liquid Waterproofing Membrane" />

        <motion.div {...fadeInUp}>
          <ImageWithContentLeft alt="Liquid Waterproofing Membrane"
            imageUrl={l1}
            title="Description"
            content="Liquid-applied membrane (LAM) is a monolithic, fully-bonded, liquid-based coating suitable for many waterproofing and roofing applications. The coating cures to form a rubber-like elastomeric waterproof membrane and may be applied over many substrates, including asphalt, bitumen and concrete."
          />
        </motion.div>

        <motion.div {...fadeInUp}>
          <TitleWithContent title="Why Liquid Waterproofing Membrane is Important">
            <Paragraph content="Liquid membranes offer unmatched versatility and efficiency, especially for complex or irregular surfaces. Their seamless nature eliminates weak points like joints and seams, which are prone to leakage. These membranes provide:" />
            <CustomLists
              items={[
                [
                  "Long-lasting protection against water penetration",
                  "Flexibility to accommodate surface movement",
                  "Excellent adhesion to various substrates",
                  "UV and chemical resistance",
                  "A fast and cost-effective installation process",
                ],
              ]}
            />
          </TitleWithContent>
        </motion.div>

        <motion.div {...fadeInUp}>
          <TitleWithContent title="Applications of Liquid Waterproofing Membrane">
            <ListWithDescription
              items={[
                {
                  title: "Flat Roofs:",
                  content: [
                    "Ideal for flat roofs to create a continuous and seamless waterproofing layer, preventing water seepage.",
                  ],
                },
                {
                  title: "Basements:",
                  content: [
                    "Applied to basement walls and floors to create a waterproof barrier, preventing moisture infiltration.",
                  ],
                },
                {
                  title: "Balconies and Terraces:",
                  content: [
                    "Provides a durable waterproof coating for balconies and terraces, safeguarding against weathering and moisture.",
                  ],
                },
                {
                  title: "Wet Areas:",
                  content: [
                    "Suitable for wet areas like bathrooms and kitchens, forming a seamless waterproofing layer around fixtures and surfaces.",
                  ],
                },
                {
                  title: "Podiums and Decks:",
                  content: [
                    "Ensures reliable waterproofing for podiums and decks, protecting underlying structures from water damage.",
                  ],
                },
                {
                  title: "Concrete Structures:",
                  content: [
                    "Used on various concrete structures, including bridges and retaining walls, to enhance water resistance.",
                  ],
                },
              ]}
            />
          </TitleWithContent>
        </motion.div>

        <motion.div {...fadeInUp}>
          <TitleWithContent title="Common Types of Liquid Waterproofing Membrane">
            <ListWithDescription
              layout="stacked"
              titleListType="disc"
              items={[
                {
                  title: "Polyurethane-Based:",
                  content: [
                    "Offers excellent flexibility and durability, suitable for various applications.",
                  ],
                },
                {
                  title: "Bitumen-Modified:",
                  content: [
                    "Combines the waterproofing properties of bitumen with the flexibility of polymers.",
                  ],
                },
                {
                  title: "Acrylic-Based:",
                  content: [
                    "Provides UV resistance and is often used for exposed applications.",
                  ],
                },
                {
                  title: "Silicone-Based:",
                  content: [
                    "Known for high elasticity and resistance to extreme temperatures.",
                  ],
                },
              ]}
            />
          </TitleWithContent>
        </motion.div>

        <motion.div {...fadeInUp}>
          <TitleWithContent title="How Liquid Waterproofing Works?">
            <Paragraph content="Understanding the process of applying liquid waterproofing membranes can provide insights into their effectiveness. Let’s explore the key steps:" />
            <ListWithDescription
              layout="stacked"
              titleListType="decimal"
              items={[
                {
                  title: "Surface Preparation",
                  content: [
                    "Before applying the liquid membrane, the surface needs to be properly prepared. This typically involves cleaning the area to remove dirt, debris, and any loose or damaged material. Any cracks or defects should be repaired, ensuring a smooth and stable base for the membrane.",
                  ],
                },
                {
                  title: "Application Process",
                  content: [
                    "Depending on the product and surface requirements, the liquid waterproofing membrane is applied using various methods such as brushing, spraying, or roller application. Multiple coats may be needed to achieve the desired thickness and ensure complete coverage. Careful attention is given to corners, edges, and details to ensure a watertight seal.",
                  ],
                },
                {
                  title: "Curing and Drying Time",
                  content: [
                    "After application, the liquid membrane requires a specific curing and drying time. This allows the membrane to bond and form a solid, impermeable layer. The duration can vary depending on the product, ambient conditions, and thickness of the applied membrane. Following the manufacturer’s instructions regarding curing and drying times is crucial for optimal results.",
                  ],
                },
              ]}
            />
          </TitleWithContent>
        </motion.div>

        <motion.div {...fadeInUp}>
          <TitleWithContent title="Why Choose Us for Liquid Waterproofing Membrane Application?">
            <Paragraph
              content={
                <>
                  At <span className="span-text">Sheel Waterproofing</span>, we offer expertise, precision, and reliability in applying liquid waterproofing systems tailored to your project’s
                  needs. We use only premium, industry-approved products applied by trained professionals to ensure:
                </>
              }
            />
            <CustomLists
              listStyle="none"
              layout="stacked"
              items={[
                [
                  "Seamless, watertight protection for all surfaces",
                  "Proper surface preparation and skilled application",
                  "Timely project completion with minimal disruption",
                  "Long-term performance and warranty-backed systems",
                ],
              ]}
              bullets={[["🔧", "🧰", "⏱️", "🛡️"]]}
            />
            <Paragraph
              content={
                <>
                  We understand that every structure is unique—and so is every waterproofing solution. Let us help protect your investment with proven liquid membrane systems that last.
                  <br /><br />
                  <a href="/contact" className="text-custom-hov underline">Contact us today</a> to schedule a consultation or inspection.
                </>
              }
            />
          </TitleWithContent>
        </motion.div>
      </div>
    </>
  )
}

export default LiquidWaterproofingMembrane
